import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'

import {
  awaitRun,
  atResolution,
  estimateCredits,
  hasResolutions,
  resolutionOf,
  resolutionsOf,
  shapesFor,
  sizeFor,
  forgetRun,
  listRuns,
  personalProject,
  pictureModels,
  startPictures,
  type HistoryRun,
  type PictureModel,
  type PictureSize,
  type RunPicture,
} from '@shared/services/pictureService'
import {
  copyProjectFiles,
  deleteProjectFile,
  listProjectFiles,
  unlistReferences,
  uploadProjectFile,
} from '@shared/services/projectFileService'
import { sendToMiroQueue } from '@shared/services/miroQueue'
import { getSkillService } from '@shared/services/skillService'
import type {
  CanvasDocument,
  CanvasEdge,
  CanvasNode,
} from '@shared/services/canvasService'
import type { ProjectFile } from '@shared/services/projectFileService'
import type { ImageRef } from '@shared/services/imageRefs'
import './generate.css'
import { WwLoaderFill } from '@shared/components/WwLoader'
import { CopyGlyph, TickGlyph } from '../components/CopyGlyphs'
import { putClip } from '@shared/services/projectClip'
import { createConversation } from '@shared/services/chatService'
import { saveCanvas } from '@shared/services/canvasService'
import { openProjectLater } from '../openProject'
import { HoldButton } from '../components/HoldButton'
import { PictureViewer } from '../components/PictureViewer'

/**
 * Making pictures without a board
 * -------------------------------------------------------------
 * The same work the canvas card does, for somebody who does not want a canvas:
 * a prompt, the settings, references, and everything made so far down the
 * right-hand side with the prompt that made it still attached.
 *
 * WHY THE FORM DOES NOT SCROLL
 *
 * Everything on the left is one decision — what to make and what to make it
 * from — and Run is the end of that sentence. A form that scrolls can put its
 * own button off screen, so the settings scroll inside themselves if they must
 * and Run stays where it was put.
 *
 * WHY THE HISTORY CARRIES ITS SETTINGS
 *
 * A picture on its own cannot be repeated, argued with, or explained to
 * anybody a week later — "which model was that?" has no answer. The run
 * already records what it was made with, so showing it costs nothing and
 * turns a wall of pictures into a record of decisions.
 *
 * WHERE ANY OF THIS IS KEPT
 *
 * A hidden project, one per person. Not a new table and a new bucket: storing
 * a picture, signing a link, naming it, billing it and finding it again all
 * exist already and are keyed on a project. A parallel set would mean writing
 * each of them twice and giving the naming scheme a third spelling — and every
 * extra spelling is another place for the defect this project keeps finding,
 * where one column holds two conventions and only one of them can be read.
 */

const MAX_REFERENCES = 8

/** A run that has been sent and not yet come back, and what it was asked for. */
interface Pending {
  id: number
  /** Known once the run has been started; what a reload finds it by. */
  runId?: string
  prompt: string
  count: number
  /** The shape asked for, so the empty frames are the shape that is coming. */
  shape: number
  /** Stops waiting for it, when it is cancelled with its × (2026-10-15). */
  stop: AbortController
}

/**
 * The type a picture travels under while being dragged from the history into
 * the reference box. Its own name rather than `text/plain`, so a stray drag of
 * selected text can never be mistaken for one.
 */
const DRAG_TYPE = 'application/x-wonderworks-picture'

/**
 * A picture chosen as a reference.
 *
 * Deliberately thinner than a `ProjectFile`: a reference can arrive from an
 * upload, from the picker, or dragged out of the history on the right, and
 * only these four things are true of all three. Making the history hand over a
 * whole `ProjectFile` would have meant a second fetch to build something the
 * page never reads.
 */
interface Chosen {
  id: string
  store: 'library' | 'project'
  url: string
  title: string
}

export function GeneratePage() {
  /** The column of runs, scrolled back to the top when Run is pressed. */
  const historyBox = useRef<HTMLElement | null>(null)
  /** A picture opened full screen (PictureViewer). */
  const [viewing, setViewing] = useState<RunPicture | null>(null)
  const [project, setProject] = useState('')
  const [models, setModels] = useState<PictureModel[]>([])
  const [error, setError] = useState('')

  const [prompt, setPrompt] = useState('')
  const [modelSlug, setModelSlug] = useState('')
  /** What was picked. `size` below is what this model can actually make. */
  const [sizeAsked, setSize] = useState('auto')
  const [quality, setQuality] = useState('auto')
  const [count, setCount] = useState(1)

  /**
   * The references chosen right now.
   *
   * Only for this visit. Every one of them is stored the moment it is
   * uploaded — leaving the page does not throw the file away, it puts it in
   * the history behind the plus. What does not survive is the *selection*,
   * which is a statement about what is being made now and has no meaning
   * tomorrow.
   */
  const [references, setReferences] = useState<Chosen[]>([])
  const [picking, setPicking] = useState(false)
  const [library, setLibrary] = useState<ProjectFile[]>([])
  /** The picker's list has been read at least once (2026-10-09: it opened empty, then filled). */
  const [libraryRead, setLibraryRead] = useState(false)
  /** Something is being dragged over the reference box right now. */
  const [dragging, setDragging] = useState(false)

  const [history, setHistory] = useState<HistoryRun[]>([])
  const [live, setLive] = useState({ live: 0, limit: 5 })
  /**
   * The runs in flight, each carrying what it was asked for.
   *
   * Not a count, and not a list of ids. A placeholder has to show the prompt
   * and the number of pictures *that run* asked for — and while one is going
   * the person is very likely typing the next one, which is the whole point of
   * running several. Reading the form for that would mean the first row
   * silently rewriting itself to describe the second: two runs sharing one
   * set of values, which is this project's oldest defect in a new costume.
   */
  const [making, setMaking] = useState<Pending[]>([])
  /** Which run is being turned into a board, so its button can say so. */
  const [starting, setStarting] = useState('')

  const upload = useRef<HTMLInputElement>(null)

  const model = useMemo(
    () => models.find((one) => one.slug === modelSlug) ?? models[0],
    [models, modelSlug],
  )
  const size = sizeFor(model, sizeAsked)

  /* ---------- opening ---------- */

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [id, list] = await Promise.all([personalProject(), pictureModels()])
        if (cancelled) return
        setProject(id)
        setModels(list)
        setModelSlug((was) => was || list[0]?.slug || '')
        // The picker's list, read now in the background, so the plus opens
        // on it rather than on a wait (2026-10-09).
        void listProjectFiles(id, { thumbs: true, copies: false })
          .then((rows) => {
            if (cancelled) return
            setLibrary(rows)
            setLibraryRead(true)
          })
          .catch(() => {})
        const runs = await listRuns(id)
        if (cancelled) return
        setHistory(runs)

        /*
         * Runs still going when the page was opened -- a reload mid-run.
         *
         * They used to show as "Nothing came back": a run with no pictures
         * yet looks exactly like one that failed, and nothing waited for it.
         * Their frames go back up with the loader, and each is waited on and
         * the list read again when it lands, as if it had been started here.
         */
        const going = runs.filter((run) => run.status === 'queued' || run.status === 'running')
        for (const run of going) {
          const pending: Pending = {
            id: Date.now() + Math.random(),
            runId: run.id,
            prompt: String(run.params.prompt ?? ''),
            count: Math.max(1, Number(run.params.n) || 1),
            shape: ratioOf(String(run.params.size ?? '')) ?? 1,
            stop: new AbortController(),
          }
          setMaking((prev) => [...prev, pending])
          void (async () => {
            try {
              await awaitRun(run.id, pending.stop.signal)
              if (pending.stop.signal.aborted) return
              if (!cancelled) setHistory(await listRuns(id))
            } catch (err) {
              console.warn('could not follow a run left going', run.id, err)
            } finally {
              if (!cancelled) setMaking((prev) => prev.filter((one) => one.id !== pending.id))
            }
          })()
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'This page could not load')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /* ---------- references ---------- */

  /**
   * The one place a reference is added.
   *
   * Every route in — the picker, an upload, a paste, a picture dragged out of
   * the history — ends here, so the limit and the no-duplicates rule are
   * stated once. Two places enforcing one rule is how they come to disagree.
   */
  const take = useCallback((one: Chosen) => {
    setReferences((prev) =>
      prev.some((other) => other.id === one.id) || prev.length >= MAX_REFERENCES
        ? prev
        : [...prev, one],
    )
  }, [])

  /**
   * Take a picture out of the list behind the plus, for good.
   *
   * Asked about first, because it cannot be undone and because it reaches
   * further than this list: a run that used this picture as a reference will
   * no longer be able to show it either. That is what deleting it means, and
   * hiding it here while leaving the bytes would be a lie about the button.
   */
  const forget = useCallback(
    async (one: ProjectFile) => {
      if (!project) return
      const sure = window.confirm(
        'Delete this picture? It goes for good, and any run that used it as a reference will no longer show it.',
      )
      if (!sure) return
      try {
        await deleteProjectFile(project, one.id)
        setLibrary((prev) => prev.filter((other) => other.id !== one.id))
        setReferences((prev) => prev.filter((other) => other.id !== one.id))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That picture could not be deleted')
      }
    },
    [project],
  )

  const openPicker = useCallback(async () => {
    if (!project) return
    setPicking(true)
    try {
      // The tiles are drawn from the copies in R2 (`thumbs`), not from the
      // full-size files in Supabase: much quicker to open (2026-10-05). What
      // was read when the page opened shows at once; this brings it up to date.
      setLibrary(await listProjectFiles(project, { thumbs: true, copies: false }))
    } catch {
      /* keep what is shown */
    } finally {
      setLibraryRead(true)
    }
  }, [project])

  const addFiles = useCallback(
    async (files: File[]) => {
      if (!project) return
      const pictures = files.filter((file) => file.type.startsWith('image/'))
      for (const file of pictures) {
        try {
          const stored = await uploadProjectFile(project, file, undefined, { reference: true })
          // Appended, never inserted: the order is the order they were added,
          // and somebody writing "the colour from the second one" means the
          // second one they chose.
          take({ id: stored.id, store: 'project', url: stored.url, title: stored.title })
        } catch (err) {
          setError(err instanceof Error ? err.message : 'That picture could not be added')
        }
      }
    },
    [project, take],
  )

  /**
   * Ctrl+V anywhere on the page.
   *
   * The button in the picker asks the browser for the clipboard, which it is
   * entitled to refuse; this path is the one the browser always allows,
   * because the person pressed the keys themselves.
   */
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const files = [...(event.clipboardData?.files ?? [])].filter((file) =>
        file.type.startsWith('image/'),
      )
      if (files.length === 0) return
      // Only once there is a picture: pasting text into the prompt is still
      // pasting text into the prompt.
      event.preventDefault()
      setPicking(false)
      void addFiles(files)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [addFiles])

  const fromClipboard = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read()
      const files: File[] = []
      for (const item of items) {
        const type = item.types.find((one) => one.startsWith('image/'))
        if (!type) continue
        const blob = await item.getType(type)
        files.push(
          new File([blob], `pasted.${type.split('/')[1] || 'png'}`, { type }),
        )
      }
      if (files.length === 0) {
        setError('There is no picture on the clipboard')
        return
      }
      setPicking(false)
      await addFiles(files)
    } catch {
      // Firefox has no clipboard.read at all, and Chrome refuses it without a
      // permission. Neither is worth an error the person cannot act on.
      setError('The browser would not hand over the clipboard — press Ctrl+V instead')
    }
  }, [addFiles])

  const drop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setDragging(false)

      // A picture dragged out of the history on the right.
      const carried = event.dataTransfer.getData(DRAG_TYPE)
      if (carried) {
        try {
          const one = JSON.parse(carried) as Chosen
          if (one?.id && one.url) take(one)
        } catch {
          /* not one of ours after all */
        }
        return
      }

      // Or a file dragged straight off the desktop.
      const files = [...event.dataTransfer.files]
      if (files.length > 0) void addFiles(files)
    },
    [addFiles, take],
  )

  /**
   * What the plus button offers.
   *
   * Uploads, always — they were put there on purpose. A generated picture only
   * once it has actually been used as a reference somewhere: until then it
   * lives in the history on the right, where it was made, and is dragged from
   * there. Otherwise every picture this page has ever produced would pile up
   * behind the plus and bury the handful that were chosen.
   *
   * The runs already record what went into them, so this is read rather than
   * stored. A column on `project_files` would have been a second home for the
   * same fact, and therefore a second chance for it to be wrong.
   *
   * Pictures kept from the Amazon skill live in this same personal project,
   * because Miro names a picture by where it is stored. They were kept to be
   * sent to a board, not offered here, so they are treated like generated
   * ones: listed only once something here has used one.
   */
  const usedAsReference = useMemo(() => {
    // Each name with the last time a run used it, so a picture cleared from
    // the list comes back only when it is used again after that.
    const names = new Map<string, string>()
    for (const run of history)
      for (const name of run.inputs) if ((names.get(name) ?? '') < run.createdAt) names.set(name, run.createdAt)
    return names
  }, [history])

  /*
   * Only references: what was put here on purpose (the picker's upload and
   * paste, the Miro panel's references -- marked `reference` by the Worker),
   * and anything a run here or in the Miro panel used as one. Not everything
   * in the personal project: mood board colour swatches, upscale and video
   * inputs live there too and filled this list with plain squares (2026-10-15).
   */
  const offered = useMemo(
    () =>
      library.filter((one) => {
        if (one.made_by?.reference === true) return true
        const used = usedAsReference.get(`project:${one.id}`)
        if (!used) return false
        const cleared = typeof one.made_by?.unlistedAt === 'string' ? one.made_by.unlistedAt : ''
        return used > cleared
      }),
    [library, usedAsReference],
  )

  const [clearing, setClearing] = useState(false)
  /** Empty the History in one go. The pictures stay; runs still show them. */
  const clearOffered = useCallback(async () => {
    if (!project || offered.length === 0) return
    const sure = window.confirm(
      `Clear all ${offered.length} pictures from History? They are not deleted: runs that used them still show them.`,
    )
    if (!sure) return
    setClearing(true)
    try {
      await unlistReferences(
        project,
        offered.map((one) => one.id),
      )
      setLibrary(await listProjectFiles(project, { thumbs: true, copies: false }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'History could not be cleared')
    } finally {
      setClearing(false)
    }
  }, [project, offered])

  /**
   * Turn one run into a board.
   *
   * The card arrives already wired: references on the left, the prompt and its
   * settings in the middle, what came out on the right. That wiring is the
   * point — a board is where a result gets argued with, and the argument
   * starts from what produced it rather than from an empty card somebody has
   * to fill in again from a screenshot.
   *
   * The pictures are copied into it first, and that step is not optional. A
   * board reads its pictures out of its own project's file list and nowhere
   * else — so pointing the new board at files that live in a hidden personal
   * project produced exactly what it should have: a correctly wired board of
   * empty frames. Copying is also what makes the board survive being shared,
   * since nobody else can see into a personal project.
   */
  /**
   * Copy to Project (Huanrui, 2026-10-07): the run as a piece of a board --
   * the references, the card set as it was, and what it made, wired -- written
   * down the way the library's Copy to project is (projectClip.ts). Every
   * project, open or opened later, then offers to add it; the pictures are
   * copied into whichever project says yes. It used to make a new project
   * every time.
   */
  const [copiedRun, setCopiedRun] = useState('')

  /**
   * Copy to Project, held (Huanrui, 2026-10-07): a new project with this run
   * on its board, opened in a new tab -- what "Create a project" used to do.
   *
   * The pictures are copied into it first: a board reads its pictures out of
   * its own project's file list and nowhere else. The tab is opened now, at
   * the end of the hold, while the press still counts as the person's own.
   */
  const makeProject = useCallback(
    async (run: HistoryRun) => {
      if (starting) return
      setStarting(run.id)
      const tab = openProjectLater()
      try {
        const made = await createConversation()
        const mine = [...run.references, ...run.pictures].filter((one) => one.store === 'project').map((one) => one.id)
        const [copied, skill, shapes] = await Promise.all([
          copyProjectFiles(made.id, mine),
          getSkillService()
            .getSkill('image-generation')
            .catch(() => null),
          shapesOf([...run.references, ...run.pictures]),
        ])
        await saveCanvas(made.id, boardFor(run, copied, skill, shapes))
        tab.go(`/projects/${made.id}`)
      } catch (err) {
        tab.cancel()
        setError(err instanceof Error ? err.message : 'A project could not be made')
      } finally {
        setStarting('')
      }
    },
    [starting],
  )
  const copyToProject = useCallback(
    async (run: HistoryRun) => {
      if (starting) return
      setStarting(run.id)
      try {
        const [skill, shapes] = await Promise.all([
          getSkillService()
            .getSkill('image-generation')
            .catch(() => null),
          shapesOf([...run.references, ...run.pictures]),
        ])
        // The ids are this person's own for now; the board swaps them for its copies.
        const doc = boardFor(run, {}, skill, shapes)
        const files = [...run.references, ...run.pictures].filter((one) => one.store === 'project').map((one) => one.id)
        putClip(
          'Image Generation',
          run.pictures.map((one) => ({ kind: 'file' as const, id: one.id, thumb: one.originalPath, title: one.title })),
          { nodes: doc.nodes, edges: doc.edges, files },
        )
        setCopiedRun(run.id)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'It could not be copied')
      } finally {
        setStarting('')
      }
    },
    [starting],
  )

  /**
   * Throw one run away.
   *
   * Asked about first, and the question says what actually goes: the pictures
   * this run made, for good. The references it used are left where they are,
   * because they were uploaded on purpose and belong to the person rather than
   * to this attempt.
   */
  const discardRun = useCallback(async (run: HistoryRun) => {
    const sure = window.confirm(
      'Delete this run and the pictures it made? They go for good. The references it used are kept.',
    )
    if (!sure) return
    try {
      await forgetRun(run.id)
      setHistory((prev) => prev.filter((other) => other.id !== run.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That run could not be deleted')
    }
  }, [])

  /**
   * Cancel a run still going, with the × on its frames (2026-10-15).
   *
   * For one that is stuck -- cut off by a deploy, say -- rather than waiting
   * the ten minutes until the server gives up on it. The run is deleted
   * (forgetRun), and this page stops waiting for it. If it was in fact still
   * working, what it makes arrives nowhere.
   */
  const cancelMaking = useCallback(async (one: Pending) => {
    if (!one.runId) return
    const sure = window.confirm('Cancel this run? Anything it is still making will not be kept.')
    if (!sure) return
    one.stop.abort()
    setMaking((prev) => prev.filter((other) => other.id !== one.id))
    try {
      await forgetRun(one.runId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That run could not be cancelled')
    }
  }, [])

  /* ---------- making ---------- */

  const estimate = model ? estimateCredits(model, quality, size, count, references.length) : 0
  /*
   * The only thing that stops another run being sent is the ceiling.
   *
   * It used to be "anything at all is running", which made a queue of one out
   * of a Worker that has taken runs in parallel since the day it became a
   * queue. The number here is the server's own answer from the last reply, and
   * the server refuses past five regardless — so a stale count costs a clear
   * refusal rather than an extra run.
   */
  const full = live.live >= live.limit
  const running = making.length

  const make = useCallback(async () => {
    if (!project || !model || !prompt.trim() || full) return
    setError('')
    // The column of runs back to its top, where this run's frames appear (Huanrui, 2026-10-15).
    historyBox.current?.scrollTo({ top: 0, behavior: 'smooth' })

    // The frames go down first, so the column says how many are coming and
    // where they will be before there is anything to put in them — and they
    // are stamped with what *this* run asked for, now, while it is still true.
    const mine: Pending = {
      id: Date.now() + Math.random(),
      prompt: prompt.trim(),
      count,
      shape: ratioOf(size) ?? 1,
      stop: new AbortController(),
    }
    setMaking((prev) => [...prev, mine])

    try {
      const refs: ImageRef[] = references.map((one) => ({ store: one.store, id: one.id }))
      const began = await startPictures({
        projectId: project,
        prompt: prompt.trim(),
        model: model.slug,
        size,
        quality,
        n: count,
        references: refs,
      })
      setLive({ live: began.live, limit: began.limit })
      setMaking((prev) => prev.map((one) => (one.id === mine.id ? { ...one, runId: began.runId } : one)))

      const run = await awaitRun(began.runId, mine.stop.signal)
      // Cancelled with its ×: already gone from the list, nothing to say.
      if (mine.stop.signal.aborted) return
      if (run.limit) setLive({ live: run.live, limit: run.limit })
      if (run.status !== 'succeeded') setError(run.error || 'It did not finish')

      // The whole list again rather than the one run pushed on the front: what
      // comes back is exactly what a reload would show, and two ways of
      // building the same row is two ways for them to disagree.
      setHistory(await listRuns(project))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'It did not start')
    } finally {
      setMaking((prev) => prev.filter((one) => one.id !== mine.id))
    }
  }, [count, full, model, project, prompt, quality, references, size])

  /* ---------- drawing ---------- */

  return (
    <div className="gen">
      <section className="gen__make">
        {/* Everything above the references. This is the part that gives way on
            a short screen, so that Run never has to. */}
        <div className="gen__fields">
          <div className="gen__field">
            <label className="gen__label" htmlFor="gen-prompt">
              Prompt
            </label>
            <textarea
              id="gen-prompt"
              className="gen__prompt"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="What should it make?"
              rows={7}
            />
          </div>

          <Chips
            label="Model"
            value={model?.slug ?? ''}
            options={models.map((one) => ({ value: one.slug, label: one.label }))}
            onPick={(slug) => {
              // Sizes and qualities belong to a model, so switching can strand
              // a setting the new one does not have.
              const next = models.find((one) => one.slug === slug)
              setModelSlug(slug)
              setSize(sizeFor(next, size))
              if (!next?.qualities.some((one) => one.value === quality)) setQuality('auto')
              setCount((was) => Math.min(was, next?.maxImages ?? 1))
            }}
          />

          <Chips
            label="Quality"
            value={quality}
            options={(model?.qualities ?? []).map((one) => ({
              value: one.value,
              label: one.label,
            }))}
            onPick={setQuality}
          />

          {/* Nano Banana is chosen by resolution and ratio, and has no quality:
              the quality chips above hide themselves when a model lists none. */}
          {hasResolutions(model) && model ? (
            <Chips
              label="Resolution"
              value={resolutionOf(model, size)}
              options={resolutionsOf(model).map((one) => ({ value: one, label: one }))}
              onPick={(resolution) => setSize(atResolution(model, size, resolution))}
            />
          ) : null}

          <Shapes value={size} options={shapesFor(model, size)} onPick={setSize} />

          <Chips
            label="Quantity"
            value={count}
            options={Array.from({ length: model?.maxImages ?? 1 }, (_, index) => ({
              value: index + 1,
              label: String(index + 1),
            }))}
            onPick={setCount}
          />
        </div>

        {/*
          The references take whatever height is left over, which is also what
          makes them an obvious place to drop something: an empty bordered area
          reads as "put it here" in a way a row of thumbnails does not.
        */}
        <div className="gen__field gen__field--refs">
          <span className="gen__label">
            References
            {references.length > 0 ? <em> · {references.length}</em> : null}
          </span>
          <div
            className={`gen__refbox${dragging ? ' gen__refbox--over' : ''}`}
            onDragOver={(event) => {
              event.preventDefault()
              event.dataTransfer.dropEffect = 'copy'
              if (!dragging) setDragging(true)
            }}
            onDragLeave={(event) => {
              // Leaving for a child of the box is not leaving the box.
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setDragging(false)
              }
            }}
            onDrop={drop}
          >
            {/* All one height and in the order they were added, because the
                order is the point: "the label from the second picture" has to
                mean the second one. */}
            <div className="gen__refs">
              {references.map((one, index) => (
                <span key={one.id} className="gen__ref">
                  <img src={one.url} alt={one.title || ''} onError={tryAgain} />
                  <em>{index + 1}</em>
                  <button
                    type="button"
                    onClick={() =>
                      setReferences((prev) => prev.filter((other) => other.id !== one.id))
                    }
                    title="Take this one out"
                    aria-label="Take this one out"
                  >
                    ×
                  </button>
                </span>
              ))}
              {references.length < MAX_REFERENCES ? (
                <button
                  type="button"
                  className="gen__add"
                  onClick={() => void openPicker()}
                  title="Add a reference"
                  aria-label="Add a reference"
                >
                  +
                </button>
              ) : null}
            </div>
            {references.length === 0 ? (
              <p className="gen__hint">
                Drag one over from the right, drop a file here, or paste with Ctrl+V.
              </p>
            ) : null}
          </div>
        </div>

        {error ? <p className="gen__error">{error}</p> : null}

        <button
          type="button"
          className="gen__run"
          onClick={() => void make()}
          disabled={full || !prompt.trim() || !model}
          title={
            full
              ? `${live.live} already going, here or elsewhere — wait for one to finish`
              : 'Make the pictures'
          }
        >
          {full ? (
            `${live.live} of ${live.limit} going — wait for one`
          ) : (
            <>
              Run
              {estimate > 0 ? <span>about {estimate} credits</span> : null}
            </>
          )}
        </button>

        {/* Said out loud, because the runs appear on the other side of the
            screen and a person who cannot see the count cannot tell whether
            pressing Run again is allowed. */}
        {running > 0 ? (
          <p className="gen__going">
            {running} running · up to {live.limit} at once
          </p>
        ) : null}
      </section>

      <section className="gen__history" ref={historyBox}>
        {making.map((one) => (
          <article key={one.id} className="gen__run-row">
            <div className="gen__shots gen__shots--making">
              {one.runId ? (
                <button
                  type="button"
                  className="gen__cancel"
                  title="Cancel this run"
                  aria-label="Cancel this run"
                  onClick={() => void cancelMaking(one)}
                >
                  <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
                    <path d="M2 2 L12 12 M12 2 L2 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
              ) : null}
              <div className="gen__row" style={{ height: 230 }}>
                {Array.from({ length: one.count }, (_, index) => (
                  <span
                    key={index}
                    className="gen__shot gen__shot--making"
                    style={{ width: 230 * one.shape }}
                  >
                    <WwLoaderFill label="Making a picture" size="large" />
                  </span>
                ))}
              </div>
            </div>
            <aside className="gen__about">
              <p className="gen__prompt-said">{one.prompt}</p>
            </aside>
          </article>
        ))}

        {/* A run still going is drawn by its frames above, not here: without
            pictures it would read as one that failed. */}
        {history.filter((run) => run.status !== 'queued' && run.status !== 'running').map((run) => (
          <article key={run.id} className="gen__run-row">
            {run.pictures.length > 0 ? (
              <Shots pictures={run.pictures} shape={ratioOf(String(run.params.size ?? ''))} onOpen={setViewing} />
            ) : (
              <p className="gen__failed">{run.error || 'Nothing came back'}</p>
            )}
            {/* The settings stay with the picture. A picture with no prompt
                beside it is one nobody can repeat a week later — and one whose
                references cannot be seen is one nobody can repeat at all. */}
            <About
              run={run}
              models={models}
              onTake={take}
              onProject={() => void copyToProject(run)}
              onNewProject={() => void makeProject(run)}
              starting={starting === run.id}
              copied={copiedRun === run.id}
              onForget={() => void discardRun(run)}
            />
          </article>
        ))}

        {history.length === 0 && making.length === 0 ? (
          <p className="gen__empty">Everything you make here will appear down this side.</p>
        ) : null}
      </section>

      {picking ? (
        <div className="gen__sheet" role="dialog" aria-label="Add a reference">
          <div className="gen__sheet-inner">
            <header>
              <strong>Add a reference</strong>
              <button type="button" onClick={() => setPicking(false)} aria-label="Close">
                ×
              </button>
            </header>

            <div className="gen__sources">
              <button
                type="button"
                className="gen__upload"
                onClick={() => upload.current?.click()}
              >
                Upload from this computer
              </button>
              <button type="button" className="gen__upload" onClick={() => void fromClipboard()}>
                Paste from the clipboard
              </button>
            </div>
            <input
              ref={upload}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(event) => {
                const files = [...(event.target.files ?? [])]
                event.target.value = ''
                setPicking(false)
                void addFiles(files)
              }}
            />

            {/* Only what was put here on purpose. Pictures this page made are
                in the run history on the right, and are dragged from there —
                they join this list once one of them has been used. */}
            <div className="gen__label-row">
              <p className="gen__label">History</p>
              {offered.length > 0 ? (
                <button
                  type="button"
                  className="gen__clear"
                  disabled={clearing}
                  onClick={() => void clearOffered()}
                >
                  {clearing ? 'Clearing…' : 'Clear all'}
                </button>
              ) : null}
            </div>
            <div className="gen__grid">
              {offered.map((one) => (
                <figure key={one.id} className="gen__tile">
                  <button
                    type="button"
                    className="gen__tile-pick"
                    onClick={() => {
                      take({ id: one.id, store: 'project', url: one.url, title: one.title })
                      setPicking(false)
                    }}
                  >
                    <img src={one.thumb ?? one.url} alt={one.title || ''} loading="lazy" decoding="async" onError={tryAgain} />
                  </button>
                  <button
                    type="button"
                    className="gen__tile-drop"
                    title="Delete this picture"
                    aria-label="Delete this picture"
                    onClick={() => void forget(one)}
                  >
                    ×
                  </button>
                </figure>
              ))}
              {offered.length === 0 ? <p className="gen__empty">{libraryRead ? 'Nothing here yet.' : 'Loading…'}</p> : null}
            </div>
          </div>
        </div>
      ) : null}

      {viewing ? <PictureViewer src={viewing.originalPath} onClose={() => setViewing(null)} /> : null}
    </div>
  )
}

/** Where a picture came from when someone put it here on purpose. */

function labelFor(models: PictureModel[], slug: string): string {
  return models.find((one) => one.slug === slug)?.label ?? slug
}

/** One row of chips. The same shape every setting takes on the canvas card. */
function Chips<T extends string | number>({
  label,
  value,
  options,
  onPick,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onPick: (value: T) => void
}) {
  if (options.length === 0) return null
  return (
    <div className="gen__field">
      <span className="gen__label">{label}</span>
      <div className="gen__chips">
        {options.map((one) => (
          <button
            key={String(one.value)}
            type="button"
            className={`gen__chip${one.value === value ? ' gen__chip--on' : ''}`}
            onClick={() => onPick(one.value)}
          >
            {one.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/** The shape tiles, drawn rather than named. Same as the card's. */
function Shapes({
  value,
  options,
  onPick,
}: {
  value: string
  options: PictureSize[]
  onPick: (value: string) => void
}) {
  if (options.length === 0) return null
  return (
    <div className="gen__field">
      <span className="gen__label">Ratio</span>
      {/* Five across once there are more than twelve (Nano Banana 2.1 has
          fourteen), so it is three rows like the ten-ratio models and the
          page does not jump when the model is switched. */}
      <div className={`gen__tiles${options.length > 12 ? ' gen__tiles--five' : ''}`}>
        {options.map((one) => {
          const long = Math.max(one.w, one.h) || 1
          const style =
            one.w > 0
              ? { width: `${Math.max(5, (one.w / long) * 26)}px`, height: `${Math.max(5, (one.h / long) * 26)}px` }
              : undefined
          return (
            <button
              key={one.value}
              type="button"
              className={`gen__tile-ratio${one.value === value ? ' gen__tile-ratio--on' : ''}`}
              onClick={() => onPick(one.value)}
              title={one.value === 'auto' ? 'The model chooses' : one.value}
            >
              <span
                className={`gen__shape${one.w > 0 ? '' : ' gen__shape--auto'}`}
                style={style}
                aria-hidden="true"
              />
              <span>{one.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ---------- what a run produced ---------- */

/** At most this many across before a run's pictures wrap onto another row. */
const PER_ROW = 4
/*
 * No tallest and no shortest row any more (Huanrui, 2026-09-28): every row
 * fills the column, and rows are as tall as that makes them. A cap of 440
 * stopped portrait and square pictures short of the right edge -- three 4:5
 * pictures need about 580 to reach it -- which read as a layout that had
 * given up. Wide pictures never hit it, which is why only some runs looked
 * wrong.
 */
/** Matches the gap in the stylesheet; the arithmetic below needs the number. */
const SHOT_GAP = 6

/**
 * A run's pictures, laid out the way a contact sheet is.
 *
 * Widths in proportion to each picture's own shape, and one height for the
 * whole row — so the row fills its width exactly, nothing is letterboxed and
 * nothing is cropped. It falls out of one line of arithmetic: if every width
 * is `height × ratio`, then setting `height = usable ÷ Σ ratios` makes the
 * widths add up to the space available.
 *
 * Two pictures therefore come out large and four come out smaller, which is
 * the behaviour that reads as deliberate rather than as a grid somebody
 * happened to fit pictures into. Every row reaches both edges; its height is
 * whatever that takes.
 *
 * The shapes are measured from the pictures as they load rather than asked
 * for: `auto` runs have no declared size, and a number that is sometimes a
 * guess is worse than one that is always measured. `shape` only seeds the
 * first frame so nothing visibly jumps when a size *was* declared.
 */
function Shots({ pictures, shape, onOpen }: { pictures: RunPicture[]; shape?: number; onOpen?: (picture: RunPicture) => void }) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [ratios, setRatios] = useState<Record<string, number>>({})

  useEffect(() => {
    const element = box.current
    if (!element) return
    setWidth(element.clientWidth)
    const watch = new ResizeObserver((entries) => {
      const seen = entries[0]?.contentRect.width
      if (typeof seen === 'number') setWidth(seen)
    })
    watch.observe(element)
    return () => watch.disconnect()
  }, [])

  /*
   * How tall the words beside the pictures are (About), by their content:
   * the column itself is stretched to the row, so its own height would be
   * the pictures' height again.
   */
  const [beside, setBeside] = useState(0)
  useEffect(() => {
    const about = box.current?.parentElement?.querySelector<HTMLElement>(':scope > .gen__about')
    if (!about) return
    const measure = () => {
      let bottom = 0
      for (const child of Array.from(about.children) as HTMLElement[]) {
        if (getComputedStyle(child).position === 'absolute') continue
        bottom = Math.max(bottom, child.offsetTop + child.offsetHeight)
      }
      setBeside(Math.ceil(bottom + (parseFloat(getComputedStyle(about).paddingBottom) || 0)))
    }
    measure()
    const watch = new ResizeObserver(measure)
    watch.observe(about)
    for (const child of Array.from(about.children)) watch.observe(child)
    return () => watch.disconnect()
  }, [])

  const ratioFor = (one: RunPicture) => ratios[one.id] ?? shape ?? 1

  /*
   * Up to PER_ROW across. But when that makes the pictures shorter than the
   * words beside them -- wide pictures, three or four in a row -- they go onto
   * two rows instead, the larger half first: 3 is 2 + 1, 4 is 2 + 2, 5 is
   * 3 + 2 (Huanrui, 2026-10-02). One picture stays as it is.
   */
  const rows = useMemo(() => {
    const out: RunPicture[][] = []
    for (let at = 0; at < pictures.length; at += PER_ROW) {
      out.push(pictures.slice(at, at + PER_ROW))
    }
    if (pictures.length < 2 || width <= 0 || beside <= 0) return out
    const ratioOf = (one: RunPicture) => ratios[one.id] ?? shape ?? 1
    const heightOf = (row: RunPicture[]) =>
      (width - SHOT_GAP * (row.length - 1)) / (row.reduce((sum, one) => sum + ratioOf(one), 0) || 1)
    if (out.every((row) => heightOf(row) >= beside)) return out
    const first = Math.ceil(pictures.length / 2)
    return [pictures.slice(0, first), pictures.slice(first)]
  }, [pictures, width, beside, ratios, shape])

  return (
    <div className="gen__shots" ref={box}>
      {rows.map((row, index) => {
        const total = row.reduce((sum, one) => sum + ratioFor(one), 0) || 1
        const usable = Math.max(0, width - SHOT_GAP * (row.length - 1))
        // Exactly the height that makes the widths add up to the column.
        const height = width > 0 ? usable / total : 0

        return (
          <div className="gen__row" key={index} style={height ? { height } : undefined}>
            {row.map((picture) => (
              <figure
                className="gen__shot"
                key={picture.id}
                style={height ? { width: height * ratioFor(picture) } : undefined}
              >
                <a
                  href={picture.originalPath}
                  target="_blank"
                  rel="noreferrer"
                  title="Open it full screen, or drag it into the references"
                  draggable
                  onDragStart={(event) => carry(event, picture)}
                  onClick={(event) => {
                    // Full screen on this page, not a new tab (Huanrui, 2026-10-15).
                    if (!onOpen || event.ctrlKey || event.metaKey || event.shiftKey) return
                    event.preventDefault()
                    onOpen(picture)
                  }}
                >
                  <img
                    src={picture.originalPath}
                    alt={picture.title || ''}
                    onError={tryAgain}
                    onLoad={(event) => {
                      const { naturalWidth, naturalHeight } = event.currentTarget
                      if (!naturalWidth || !naturalHeight) return
                      // Measured once. Setting it again on every load would be
                      // a state change that causes the load that sets it.
                      setRatios((prev) =>
                        prev[picture.id]
                          ? prev
                          : { ...prev, [picture.id]: naturalWidth / naturalHeight },
                      )
                    }}
                  />
                </a>
                <button
                  type="button"
                  className="gen__save"
                  title="Save it"
                  aria-label="Save it"
                  onClick={() => void save(picture)}
                >
                  <Arrow />
                </button>
              </figure>
            ))}
          </div>
        )
      })}
    </div>
  )
}

/**
 * The column beside the pictures: what was asked for, and what it was asked of.
 *
 * The references are here rather than only in the settings because they are
 * half the instruction. They are draggable for the same reason the pictures
 * are — the commonest next thing anybody does is make another one from the
 * same starting point.
 */
function About({
  run,
  models,
  onTake,
  onProject,
  onNewProject,
  starting,
  copied: copiedToProject,
  onForget,
}: {
  run: HistoryRun
  models: PictureModel[]
  onTake: (one: Chosen) => void
  onProject: () => void
  /** Copy to Project held: a new project made with it. */
  onNewProject: () => void
  starting: boolean
  /** Copied to Project, and waiting for a project to take it. */
  copied: boolean
  onForget: () => void
}) {
  const [copied, setCopied] = useState(false)
  const said = String(run.params.prompt ?? '')
  /*
   * Send to Miro (Huanrui, 2026-10-05): the run's pictures, full size, into
   * the queue the WonderWorks panel picks up. One picture goes straight onto
   * the board; several go into a frame, as any batch does (boardQueue.ts).
   */
  const [sent, setSent] = useState<'' | 'sending' | 'sent' | 'failed'>('')
  const toMiro = async () => {
    if (sent === 'sending' || run.pictures.length === 0) return
    setSent('sending')
    try {
      await sendToMiroQueue(
        'web',
        run.pictures.map((one) => ({ store: one.store, id: one.id })),
      )
      setSent('sent')
    } catch {
      setSent('failed')
    }
  }

  return (
    <aside className="gen__about">
      <div className="gen__said">
        <p className="gen__prompt-said">{said}</p>
        {said ? (
          <button
            type="button"
            className="gen__copy"
            title="Copy the prompt"
            aria-label="Copy the prompt"
            onClick={() => {
              void navigator.clipboard
                .writeText(said)
                .then(() => {
                  setCopied(true)
                  window.setTimeout(() => setCopied(false), 1200)
                })
                .catch(() => {
                  /* the browser refused; nothing useful to say about it */
                })
            }}
          >
            {copied ? <TickGlyph /> : <CopyGlyph />}
          </button>
        ) : null}
      </div>

      <span className="gen__tags">
        <em>{labelFor(models, String(run.params.model ?? ''))}</em>
        <em>{String(run.params.size ?? 'auto')}</em>
        <em>{String(run.params.quality ?? 'auto')}</em>
        {run.credits ? <em>{run.credits} credits</em> : null}
      </span>

      {/* The references it used, under what it was made with (Huanrui, 2026-10-07). */}
      {run.references.length > 0 ? (
        <div className="gen__usedrefs gen__usedrefs--room">
          {run.references.map((one) => (
            <img
              key={one.id}
              className="gen__usedref"
              src={one.originalPath}
              alt={one.title || ''}
              onError={tryAgain}
              title="Drag it into the references, or click to use it again"
              draggable
              onDragStart={(event) => carry(event, one)}
              onClick={() => onTake(asChosen(one))}
            />
          ))}
        </div>
      ) : null}
      {/* At the foot of the column, the same size, as on the upscale page
          (Huanrui, 2026-10-07). */}
      <div className="gen__acts">
        {copiedToProject ? (
          <p className="gen__sent">Copied. Open any project and it will be offered there, with its card.</p>
        ) : null}
        {sent === 'sent' ? (
          <p className="gen__sent">
            {run.pictures.length === 1 ? 'Sent.' : `${run.pictures.length} sent, as a frame.`} Open the
            WonderWorks panel in Miro to place {run.pictures.length === 1 ? 'it' : 'them'}.
          </p>
        ) : null}
        {sent === 'failed' ? <p className="gen__failed">It could not be sent to Miro.</p> : null}
        <HoldButton
          className="gen__project"
          onClick={onProject}
          onHold={onNewProject}
          disabled={starting || run.pictures.length === 0}
        >
          {starting ? 'Working…' : 'Copy to Project'}
        </HoldButton>
        <button
          type="button"
          className="gen__project"
          onClick={() => void toMiro()}
          disabled={sent === 'sending' || run.pictures.length === 0}
        >
          {sent === 'sending' ? 'Sending…' : 'Send to Miro'}
        </button>
      </div>

      {/* Bottom right, away from everything that is safe to press. */}
      <button
        type="button"
        className="gen__bin"
        onClick={onForget}
        title="Delete this run and its pictures"
        aria-label="Delete this run and its pictures"
      >
        <Bin />
      </button>
    </aside>
  )
}

/* ---------- small shared pieces ---------- */

/** Roughly a card's width, so nothing lands on top of anything else. */
const COLUMN = 420
const STACKED = 240
/** The gap under each picture in a column; STACKED was 200 of picture and 40 of this. */
const STACK_STEP_GAP = 40

/**
 * One run, drawn as a board.
 *
 * References down the left into the card's numbered ports, the card in the
 * middle carrying exactly what was asked for, and what came out down the
 * right. The ids are made up here and never leave this document, so they can
 * be as plain as this.
 */
/**
 * Width over height of each picture, by id, read by loading it.
 *
 * Every one is loaded in parallel and none may hold the button up for long:
 * a picture that does not answer within a few seconds is simply given the
 * old 260x200, which is the worst this can be.
 */
async function shapesOf(pictures: RunPicture[]): Promise<Map<string, number>> {
  const found = new Map<string, number>()
  await Promise.all(
    pictures.map(
      (picture) =>
        new Promise<void>((done) => {
          const img = new Image()
          const stop = setTimeout(done, 4000)
          img.onload = () => {
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              found.set(picture.id, img.naturalWidth / img.naturalHeight)
            }
            clearTimeout(stop)
            done()
          }
          img.onerror = () => {
            console.warn('could not read the shape of a picture', picture.id)
            clearTimeout(stop)
            done()
          }
          img.src = picture.originalPath
        }),
    ),
  )
  return found
}

/** How wide a picture is placed on the new board. Its height follows its shape. */
const PLACED_WIDTH = 260

function boardFor(
  run: HistoryRun,
  copied: Record<string, string>,
  skill: { id: string; name: string; glyph: string } | null,
  shapes: Map<string, number> = new Map(),
): CanvasDocument {
  const page = { id: 'page-1', name: 'Page 1' }
  const nodes: CanvasNode[] = []
  const edges: CanvasEdge[] = []
  const card = 'card-1'

  // Stacked by each picture's own height, so a tall one does not run into the
  // one below it the way a fixed step would let it.
  const heightOf = (one: RunPicture) => {
    const shape = shapes.get(one.id)
    return shape ? Math.round(PLACED_WIDTH / shape) : 200
  }
  let below = 0
  run.references.forEach((one, index) => {
    const id = `in-${index + 1}`
    const height = heightOf(one)
    nodes.push({
      id,
      type: 'image',
      page: page.id,
      position: { x: 0, y: below },
      width: PLACED_WIDTH,
      height,
      data: { ...pointAt(one, copied), title: one.title || 'Reference' },
    })
    // The port a reference goes to is its place in the order, which is what
    // "the colour from the second picture" depends on.
    edges.push({ id: `w-${id}`, source: id, target: card, targetHandle: `ref-${index}` })
    below += Math.max(height, STACKED - STACK_STEP_GAP) + STACK_STEP_GAP
  })

  nodes.push({
    id: card,
    type: 'picture',
    page: page.id,
    position: { x: COLUMN, y: 0 },
    width: 340,
    data: {
      // What the rail puts on a card it drops. Without these the card is
      // nameless, and a folded one is a bar with nothing written on it.
      ...(skill ? { skillRef: skill.id, name: skill.name, glyph: skill.glyph } : {}),
      prompt: String(run.params.prompt ?? ''),
      model: String(run.params.model ?? ''),
      size: String(run.params.size ?? 'auto'),
      quality: String(run.params.quality ?? 'auto'),
      n: Number(run.params.n) || 1,
      open: true,
    },
  })

  below = 0
  run.pictures.forEach((one, index) => {
    const id = `out-${index + 1}`
    const height = heightOf(one)
    nodes.push({
      id,
      type: 'image',
      page: page.id,
      position: { x: COLUMN * 2, y: below },
      width: PLACED_WIDTH,
      height,
      data: {
        ...pointAt(one, copied),
        title: one.title || 'Made here',
        // The run's prompt and settings, for the corner button on the board.
        // The copies have new ids there, so the board could not look them up.
        made: {
          prompt: String(run.params.prompt ?? ''),
          model: String(run.params.model ?? ''),
          size: String(run.params.size ?? 'auto'),
          quality: String(run.params.quality ?? 'auto'),
          credits: run.credits,
          at: run.createdAt,
        },
      },
    })
    edges.push({ id: `w-${id}`, source: card, target: id, targetHandle: 'in' })
    below += Math.max(height, STACKED - STACK_STEP_GAP) + STACK_STEP_GAP
  })

  return { pages: [page], nodes, edges }
}

/**
 * How a node names its picture.
 *
 * The library is everybody's, so a library picture is named where it stands.
 * A project picture is named by the copy that now lives in this board's own
 * project — the id it had somewhere else would resolve to nothing here.
 */
function pointAt(
  picture: RunPicture,
  copied: Record<string, string>,
): { assetId: string } | { fileId: string } {
  if (picture.store === 'library') return { assetId: picture.id }
  return { fileId: copied[picture.id] ?? picture.id }
}

function asChosen(picture: RunPicture): Chosen {
  return {
    id: picture.id,
    store: picture.store,
    url: picture.originalPath,
    title: picture.title,
  }
}

/** Put a picture on the drag, under our own type rather than as a URL. */
function carry(event: DragEvent<HTMLElement>, picture: RunPicture) {
  event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(asChosen(picture)))
  event.dataTransfer.effectAllowed = 'copy'
}

/**
 * The declared shape of a run, if it declared one.
 *
 * `auto` and anything unparseable return nothing at all rather than 1: a
 * square is a claim, and the pictures themselves will answer in a moment.
 */
function ratioOf(size: string): number | undefined {
  const parts = /^(\d+)x(\d+)$/.exec(size.trim())
  if (!parts) return undefined
  const wide = Number(parts[1])
  const tall = Number(parts[2])
  return wide > 0 && tall > 0 ? wide / tall : undefined
}

/**
 * Save a picture to disk.
 *
 * Fetched and handed over as a blob rather than linked with `download`: the
 * link is signed and served from another origin, where `download` is ignored
 * and the browser navigates to the picture instead. If the fetch is refused
 * that navigation is still better than nothing, so it is the fallback.
 */
async function save(picture: RunPicture) {
  const name = nameOf(picture)
  try {
    const response = await fetch(picture.originalPath)
    if (!response.ok) throw new Error(String(response.status))
    const blob = await response.blob()
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = name
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(link.href), 10_000)
  } catch {
    window.open(picture.originalPath, '_blank', 'noopener')
  }
}

function nameOf(picture: RunPicture): string {
  const path = picture.originalPath.split('?')[0] ?? ''
  const last = path.split('/').pop() ?? ''
  if (/\.[a-z0-9]{2,5}$/i.test(last)) return last
  return `${picture.title || 'picture'}.png`
}

function Arrow() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <path
        d="M8 2.6v7.2m0 0 2.8-2.8M8 9.8 5.2 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.2 12.6h9.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}


function Bin() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path
        d="M3 4.3h10M6.4 4.3V3.1h3.2v1.2M4.4 4.3l.6 8.1a1 1 0 0 0 1 .9h4a1 1 0 0 0 1-.9l.6-8.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.8 6.6v4.2M9.2 6.6v4.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * A picture Storage could not give just then -- "too many connections" when
 * it is crowded (2026-10-15) -- is asked for again, twice, a moment later,
 * rather than left as a broken picture until the page is reloaded.
 */
function tryAgain(event: React.SyntheticEvent<HTMLImageElement>) {
  const img = event.currentTarget
  const tries = Number(img.dataset.tries ?? 0)
  if (tries >= 2) return
  img.dataset.tries = String(tries + 1)
  const src = img.src
  if (!src) return
  window.setTimeout(() => {
    img.src = `${src}${src.includes('?') ? '&' : '?'}again=${tries + 1}`
  }, 1500 * (tries + 1) ** 2)
}
